import React, { useState } from 'react';
import type { CourseCandidate } from '../../../../services/courseCandidatesService';
import { courseCandidatesService } from '../../../../services/courseCandidatesService';

interface CourseListProps {
  courses: CourseCandidate[];
  coursesLoading: boolean;
  onSelectCourse: (courseId: string) => void;
  onNewCourseCreated: (courseId: string) => void;
  onError: (msg: string) => void;
  loadCourses: () => Promise<void>;
}

export function CourseList({
  courses,
  coursesLoading,
  onSelectCourse,
  onNewCourseCreated,
  onError,
  loadCourses,
}: CourseListProps) {
  const [creating, setCreating] = useState(false);
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);

  return (
    <>
      {coursesLoading && (
        <div className="flex flex-col items-center justify-center h-32 mb-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue"></div>
          <p className="mt-4 text-gray-600">Loading courses...</p>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-medium text-gray-700">Courses</h3>
        <button
          disabled={creating}
          onClick={async () => {
            setCreating(true);
            try {
              const created = await courseCandidatesService.create({
                title: 'New Course',
                audience: 'Business professionals',
                cefr_level: 'B1-B2',
              });
              await loadCourses();
              onNewCourseCreated(created.course_id);
            } catch (e: unknown) {
              onError(e instanceof Error ? e.message : 'Failed to create course');
            } finally {
              setCreating(false);
            }
          }}
          className="px-3 py-1 text-white text-sm rounded hover:opacity-90 shrink-0 disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
        >
          {creating ? 'Creating...' : 'New Course'}
        </button>
      </div>

      <div className="bg-white border rounded p-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #1a2332 0%, #2d3748 100%)' }}>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm">Course</th>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm w-20">CEFR</th>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm">Updated</th>
              <th className="px-3 py-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {courses.map((course) => (
              <tr
                key={course.course_id}
                className="cursor-pointer hover:bg-gray-50 transition-all"
                style={{ boxShadow: 'inset 0 0 0 0 #01bcd4' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'inset 4px 0 0 0 #01bcd4'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 0 #01bcd4'; }}
                onClick={() => onSelectCourse(course.course_id)}
              >
                <td className="px-3 py-3">
                  <div className="font-medium text-gray-900 text-[15px]">{course.title}</div>
                  {course.audience && <div className="text-xs text-gray-400 mt-0.5">{course.audience}</div>}
                </td>
                <td className="px-3 py-3 text-gray-500 font-mono text-sm">{course.cefr_level}</td>
                <td className="px-3 py-3 text-gray-500 text-sm">{new Date(course.updatedAt).toLocaleDateString()}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Delete course"
                    onClick={(e) => { e.stopPropagation(); setArchiveConfirmId(course.course_id); }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
            {courses.length === 0 && !coursesLoading && (
              <tr><td className="px-3 py-4 text-gray-500" colSpan={4}>No courses yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {archiveConfirmId && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true" onClick={() => setArchiveConfirmId(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-4 border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Delete course?</h3>
            <p className="text-sm text-gray-600">This will archive the course and remove it from the pipeline. The data will not be permanently deleted.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => setArchiveConfirmId(null)}>Cancel</button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700"
                onClick={async () => {
                  try {
                    await courseCandidatesService.archive(archiveConfirmId);
                    setArchiveConfirmId(null);
                    await loadCourses();
                  } catch (e: unknown) {
                    onError(e instanceof Error ? e.message : 'Failed to delete course');
                    setArchiveConfirmId(null);
                  }
                }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
